import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useContext, useEffect, useState } from 'react';

import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import { analytics } from 'apps/marsinet/src/firebase';
import { logEvent } from 'firebase/analytics';

export interface ModalAddDeviceToOrganizationProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  addDeviceToInstitutionFunction: (values: { organization_id: string }) => void;
  device_id: string;
}

interface Organization {
  o_id: string;
  name: string;
}

const ModalAddDeviceToOrganization: React.FC<
  ModalAddDeviceToOrganizationProps
> = ({ visible, setVisible, addDeviceToInstitutionFunction, device_id }) => {
  const { t } = useTranslation();
  const { access_token, setError } = useContext(MarsinetContext);
  const [institutions, setInstitutions] = useState<Organization[]>([]);
  const [selectedInstitution, setSelectedInstitution] = useState<string | null>(
    null
  );

  const handleChangeSelectedDevice = (e: { value: string }) => {
    setSelectedInstitution(e.value);
  };

  const onpagehide = () => {
    setSelectedInstitution(null);
    setVisible(false);
  };

  useEffect(() => {
    const fetchInstitutions = async () => {
      if (!visible) return;

      try {
        const response =
          await apiServices.devicesApi.devicesControllerFindAllOrganizations(
            device_id
          );
        const associatedOrgIds = new Set(
          response.data.map((org: Organization) => org.o_id)
        );

        const allOrgsResponse =
          await apiServices.userApi.usersControllerGetOrganizations();
        const allOrgsData = allOrgsResponse.data;

        const filteredOrganizations = allOrgsData.filter(
          (org: Organization) => !associatedOrgIds.has(org.o_id)
        );
        setInstitutions(filteredOrganizations);
      } catch (error) {
        if (error.response?.status !== 401) {
          setError(true);
          logEvent(analytics, 'error_marsinet', {
            error_message: error.message,
            error_code: error.response?.status,
          });
        }
      }
    };

    if (access_token) {
      fetchInstitutions();
    }
  }, [visible, access_token, device_id]);

  const saveInstitution = () => {
    if (!selectedInstitution) return;
    addDeviceToInstitutionFunction({
      organization_id: selectedInstitution,
    });
    setVisible(false);
    setSelectedInstitution(null);
  };

  return (
    <Dialog
      header={t('selectInstitution')}
      visible={visible}
      className="mx-2 md:w-12 lg:w-8"
      onHide={onpagehide}
      breakpoints={{ '960px': '75vw', '641px': '100vw' }}
    >
      <div className="grid formgrid p-fluid">
        <div className="field mb-5 col-12 py-2">
          <Dropdown
            value={selectedInstitution}
            onChange={handleChangeSelectedDevice}
            options={institutions}
            optionLabel="name"
            placeholder={t('institution')}
            optionValue="o_id"
            showClear
            filter
          />
        </div>
      </div>
      <div className="flex align-items-center justify-content-end">
        <div className="">
          <Button
            id="button-create-device"
            severity="secondary"
            icon="pi pi-save"
            label={t('save')}
            className="p-button-raised"
            onClick={saveInstitution}
            disabled={!selectedInstitution}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default ModalAddDeviceToOrganization;
